<script setup>
import { ref, computed, nextTick, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useCaptain } from 'dashboard/composables/useCaptain';
import { useEscapePriority } from 'dashboard/composables/useEscapePriority';
import NextButton from 'dashboard/components-next/button/Button.vue';
import CaptainLoader from 'dashboard/components/widgets/conversation/copilot/CaptainLoader.vue';

// SuperTVGo Copilot — shared mini-chat popup for actions 2 (guided reply)
// and 4 (ask about customer). Action 2 shows an "Approve" button that hands
// the last AI message back to the caller (inserted into the Private Note
// editor); action 4 is read-only, everything stays in this dialog.
const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  mode: {
    type: String,
    required: true,
    validator: value => ['guided_reply', 'customer_context'].includes(value),
  },
});

const emit = defineEmits(['close', 'approve']);

const { t } = useI18n();
const { guidedReply, customerContext, followUp } = useCaptain();

const isGuidedReply = computed(() => props.mode === 'guided_reply');

// Modal.vue (woot-modal) already closes on Escape itself — this only tells
// ConversationHeader's global Escape handler to stand down, so pressing
// Escape closes this popup instead of also closing the ticket underneath it.
useEscapePriority(computed(() => props.show));

const messages = ref([]);
const userInput = ref('');
const isGenerating = ref(false);
const followUpContext = ref(null);
const messagesEndRef = ref(null);

const title = computed(() =>
  isGuidedReply.value
    ? t('COPILOT_POPUP.GUIDED_REPLY_TITLE')
    : t('COPILOT_POPUP.CUSTOMER_CONTEXT_TITLE')
);
const inputPlaceholder = computed(() => {
  if (messages.value.length > 0) {
    return t('COPILOT_POPUP.FOLLOW_UP_PLACEHOLDER');
  }
  return isGuidedReply.value
    ? t('COPILOT_POPUP.DIRECTION_PLACEHOLDER')
    : t('COPILOT_POPUP.QUESTION_PLACEHOLDER');
});

const lastAssistantMessage = computed(() => {
  const assistantMessages = messages.value.filter(m => m.role === 'assistant');
  return assistantMessages.at(-1)?.content || '';
});
const canApprove = computed(
  () =>
    isGuidedReply.value && !!lastAssistantMessage.value && !isGenerating.value
);

const scrollToBottom = () => {
  nextTick(() => {
    messagesEndRef.value?.scrollIntoView({ behavior: 'smooth' });
  });
};

const runInitialAction = text => {
  if (isGuidedReply.value) return guidedReply(text);
  return customerContext(text);
};

const send = async () => {
  const text = userInput.value.trim();
  if (!text || isGenerating.value) return;

  messages.value.push({ role: 'user', content: text });
  userInput.value = '';
  isGenerating.value = true;
  scrollToBottom();

  const result = followUpContext.value
    ? await followUp({ followUpContext: followUpContext.value, message: text })
    : await runInitialAction(text);

  isGenerating.value = false;

  if (result.message) {
    messages.value.push({ role: 'assistant', content: result.message });
    followUpContext.value = result.followUpContext || followUpContext.value;
  }
  scrollToBottom();
};

const handleApprove = () => {
  emit('approve', lastAssistantMessage.value);
};

const handleClose = () => {
  emit('close');
};

// The modal's own v-if unmounts/remounts the DOM on show/hide, but this
// component instance (and its refs) stays alive across that — reset the
// chat manually so reopening never bleeds a previous session's messages in.
watch(
  () => props.show,
  (isShown, wasShown) => {
    if (!isShown && wasShown) {
      messages.value = [];
      userInput.value = '';
      followUpContext.value = null;
      isGenerating.value = false;
    }
  }
);
</script>

<template>
  <woot-modal :show="show" size="modal-chat" @close="handleClose">
    <div class="flex flex-col">
      <woot-modal-header :header-title="title" />
      <div class="modal-content w-full pt-2 px-5 pb-4">
        <!-- min-h only once there's something to show. It used to be
             unconditional, reserving 160px of blank space before the first
             message existed — on a phone that pushed the actual input field
             (the only thing worth interacting with at that point) far down
             past a wall of empty space. -->
        <div
          v-if="messages.length || isGenerating"
          class="flex flex-col gap-3 overflow-y-auto min-h-[6rem] max-h-[20rem] md:min-h-[10rem] md:max-h-[24rem] mb-3"
        >
          <div
            v-for="(msg, index) in messages"
            :key="index"
            class="flex"
            :class="msg.role === 'user' ? 'justify-end' : 'justify-start'"
          >
            <div
              class="max-w-[85%] rounded-xl px-3 py-2 text-sm whitespace-pre-wrap"
              :class="
                msg.role === 'user'
                  ? 'bg-n-brand text-white'
                  : 'bg-n-slate-3 text-n-slate-12'
              "
            >
              {{ msg.content }}
            </div>
          </div>
          <div
            v-if="isGenerating"
            class="flex items-center gap-2 text-n-iris-10"
          >
            <CaptainLoader class="size-4" />
            <span class="text-sm">
              {{ t('COPILOT_POPUP.THINKING') }}
            </span>
          </div>
          <div ref="messagesEndRef" />
        </div>
        <form class="flex items-end w-full gap-2" @submit.prevent="send">
          <textarea
            v-model="userInput"
            rows="2"
            class="flex-1 reset-base !min-h-0 !resize-none text-sm rounded-lg outline outline-1 outline-n-weak focus:outline-n-brand border-none bg-n-alpha-1 text-n-slate-12 placeholder:text-n-slate-10 !p-2"
            :placeholder="inputPlaceholder"
            @keydown.enter.exact.prevent="send"
          />
          <NextButton
            type="submit"
            sm
            :label="t('COPILOT_POPUP.SEND')"
            :disabled="isGenerating || !userInput.trim()"
          />
        </form>
        <div class="flex justify-end gap-2 mt-4">
          <NextButton
            faded
            slate
            sm
            :label="t('COPILOT_POPUP.CLOSE')"
            @click="handleClose"
          />
          <NextButton
            v-if="isGuidedReply"
            sm
            class="bg-n-iris-9 text-white"
            solid
            :disabled="!canApprove"
            :label="t('COPILOT_POPUP.APPROVE')"
            @click="handleApprove"
          />
        </div>
      </div>
    </div>
  </woot-modal>
</template>
